/**
 * A falha com saída (E200.F3, GUS-220, ADR-0143 §4): a mensagem diz o que houve, e os botões dizem
 * por onde sair — "Tentar de novo" refaz o pedido que falhou, "Voltar ao caso"/"Voltar aos casos"
 * tira a pessoa do beco, e "Revisar na mesa" leva ao ponto editável quando a correção é dela. Nunca
 * um erro sem botão: a pessoa não fica presa numa tela que só diz "não foi possível".
 *
 * Os rótulos têm o padrão de `ptBR.falhaComSaida`; quem chama troca quando a saída tem outro nome
 * (por exemplo, "Voltar à mesa"). O `data-testid` da raiz dá o prefixo dos botões (`-tentar`,
 * `-voltar`, `-revisar`).
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import { cn } from '@/lib/utils';

import { Button } from './Button';

const t = ptBR.falhaComSaida;

/** Para onde a pessoa sai, com o rótulo quando o padrão não serve. */
export interface SaidaDaFalha {
  para: string;
  rotulo?: string;
}

export interface FalhaComSaidaProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** O que houve, onde e o que fazer, em uma ou duas frases. */
  mensagem: string;
  /** Refaz o pedido que falhou; sem ele, não há "Tentar de novo" (a ação está noutro botão). */
  onTentarDeNovo?: (() => void) | undefined;
  /** A nova tentativa já está em curso. */
  tentando?: boolean;
  /** A saída do beco: o caso ou a lista de casos. */
  voltar?: SaidaDaFalha | undefined;
  /** O ponto editável que resolve a falha, quando a correção é da pessoa. */
  revisar?: SaidaDaFalha | undefined;
}

export const FalhaComSaida = forwardRef<HTMLDivElement, FalhaComSaidaProps>(function FalhaComSaida(
  {
    mensagem,
    onTentarDeNovo,
    tentando = false,
    voltar,
    revisar,
    className,
    'data-testid': testId = 'falha-com-saida',
    ...props
  }: FalhaComSaidaProps & { 'data-testid'?: string },
  ref,
) {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn('space-y-sc-2', className)}
      data-testid={testId}
      {...props}
    >
      <p className="text-body-sm text-danger-700">{mensagem}</p>
      {onTentarDeNovo !== undefined || voltar !== undefined || revisar !== undefined ? (
        <div className="flex flex-wrap items-center gap-sc-2">
          {revisar !== undefined ? (
            <Button asChild size="sm">
              <Link to={revisar.para} data-testid={`${testId}-revisar`}>
                {revisar.rotulo ?? t.revisarNaMesa}
              </Link>
            </Button>
          ) : null}
          {onTentarDeNovo !== undefined ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={tentando}
              onClick={onTentarDeNovo}
              data-testid={`${testId}-tentar`}
            >
              {t.tentarDeNovo}
            </Button>
          ) : null}
          {voltar !== undefined ? (
            <Button asChild variant="ghost" size="sm">
              <Link to={voltar.para} data-testid={`${testId}-voltar`}>
                {voltar.rotulo ?? t.voltarAoCaso}
              </Link>
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
